import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { AutoCompleteResult, ID, Maybe, Result } from 'src/models';
import { mockFn } from 'src/tools/fn.tools';
import { InputContext } from 'src/contexts/input.context';
import { BaseComponentProps } from 'src/components/Dropdown';
import { DropdownMenu } from 'src/components/Dropdown/DropdownMenu';
import { InputWithIcons } from 'src/components/Inputs';
import { ifTrue } from 'src/tools/logic.tools';
import { useDebouncedFn } from 'src/hooks';
import { getCustomIsLoading } from 'src/store/loading/loading.reducer';

export function CustomAutoComplete() {
  const {
    data,
    onChange,
    classNames,
    onSubmit = mockFn,
    onDiscard = mockFn,
    isDisabled,
    isAutoFocus,
    onClick,
    customization = {},
    actions = {},
    placeholder,
  } = useContext(InputContext);

  const { isInline, isXSmall, isSmallMenu, dropdownMinWidth = 'container-width', dropdownPosition } = customization;

  const { onChangeInput, onAutocompleteRequest } = actions;

  const [results, setResults] = useState<AutoCompleteResult[]>([]);

  const isLoading = useSelector(getCustomIsLoading)('autocomplete');

  const [inputValue, setInputValue] = useState<Maybe<string>>();

  const [isOpen, setIsOpen] = useState(false);

  const skipResponse = useRef(false);

  const handleOnResults = useCallback((response: Result<AutoCompleteResult[]>) => {
    if (!response.isOK || skipResponse.current) return;

    const newResults = response.payload?.map(({ id, label = String(id) }) => ({ id, label })) ?? [];

    setResults(newResults);
  }, []);

  const sendRequest = useCallback(
    (userInput: string) => {
      if (!onAutocompleteRequest || skipResponse.current) return;

      onAutocompleteRequest(userInput, data, handleOnResults);
    },
    [onAutocompleteRequest, data, handleOnResults],
  );

  const sendRequestDebounced = useDebouncedFn(sendRequest, 600);

  useEffect(() => {
    if (isDisabled) setInputValue(undefined);
  }, [isDisabled]);

  function handleOnItemClick(id: ID) {
    setIsOpen(false);

    const item = results.find(i => i.id === id);

    if (onChangeInput) onChangeInput(item?.label);

    setInputValue(item?.label);

    onChange(item, true);
  }

  function getHandleOnClick(fn: () => void) {
    return function call() {
      fn();

      if (onClick) onClick();
    };
  }

  function handleOnChange(userInput: string) {
    setIsOpen(true);
    onChange(undefined);
    setInputValue(userInput);

    if (userInput.length >= 2 && onAutocompleteRequest) {
      sendRequestDebounced(userInput);
      skipResponse.current = false;
    } else if (!userInput) {
      setResults([]);
      skipResponse.current = true;
    }

    if (onChangeInput) onChangeInput(userInput);
  }

  function handleOnRequestOpenClose(state: boolean) {
    setIsOpen(state);

    if (!state) onDiscard();
  }

  function handleOnDiscard() {
    setIsOpen(false);

    onDiscard();
  }

  function handleOnSubmit() {
    setIsOpen(false);

    onSubmit();
  }

  function renderBaseComponent(baseComponentProps: BaseComponentProps) {
    const iconId = baseComponentProps.isOpen ? 'chevron-up' : 'chevron-down';

    return (
      <InputWithIcons
        className={classNames?.input}
        onChange={handleOnChange}
        onDiscard={handleOnDiscard}
        onSubmit={handleOnSubmit}
        onClick={getHandleOnClick(baseComponentProps.onClick)}
        isLoading={isLoading}
        value={inputValue}
        onFocus={baseComponentProps.onFocus}
        onBlur={baseComponentProps.onBlur}
        isDisabled={isDisabled}
        autoFocus={isAutoFocus}
        rightIconId={ifTrue(results.length, iconId)}
        isXSmall={!!isXSmall}
        placeholder={placeholder}
      />
    );
  }

  return (
    <DropdownMenu
      classNames={classNames}
      items={results}
      onItemClick={handleOnItemClick}
      renderBaseComponent={renderBaseComponent}
      isOpen={isOpen && !isLoading}
      onRequestOpenClose={handleOnRequestOpenClose}
      isSmall={!!isSmallMenu}
      isInline={!!isInline}
      dropdownMinWidth={dropdownMinWidth}
      position={dropdownPosition}
    />
  );
}
